import { defaultObjectIcon, ObjectInfos } from "@dto/objects";
import { FullRightAccess, hasGlobalRightAccess, ObjectRightType } from "@dto/Users";
import { CIcon } from "@utils/icons";
import { getWsButtonStyle, TW_ListItemArrow, TW_ListItemIcon, TW_ListItemName, TW_ObjectListItem, TW_ObjectListItemContainer } from "@utils/twClasses";
import { ArrowDown2, ArrowRight2 } from "iconsax-react";
import { useEffect, useRef, useState } from "react";
import { useModelStore, useUserStore } from "src/store";
import DropLineIndicator from "./DropLineIndicator";
import ObjectConfigView from "./ObjectConfigView";
import ObjectMenuButton from "./ObjectMenuButton";
import ViewList from "./ViewList";
import { getSelectedObjectKey } from "@utils/localStorage";

interface DraggableObjectListItemProps {
    objectInfos: ObjectInfos;
    onMobileClose?: () => void;
    onDragStart?: (objectKey: string) => void;
    onDragEnd?: () => void;
    onDragOver?: (objectKey: string, position: 'top' | 'bottom' | null) => void;
    onDrop?: (draggedObjectKey: string, targetObjectKey: string, position: 'top' | 'bottom') => void;
    showDropLineTop?: boolean;
    showDropLineBottom?: boolean;
}

function DraggableObjectListItem({
    objectInfos,
    onMobileClose,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDrop,
    showDropLineTop = false,
    showDropLineBottom = false,
}: DraggableObjectListItemProps) {

    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const workspace = useModelStore(state => state.selectedWorkspace);
    const [isOpen, setIsOpen] = useState(false);
    const wsHoverColor = useModelStore(state => state.wsHoverColor);
    const wsColor = useModelStore(state => state.wsColor);
    const [isHover, setIsHover] = useState(false);
    const userRole = useModelStore(state => state.userRole);
    const dragRef = useRef<HTMLDivElement>(null);
    const user = useUserStore(state => state.user);
    const [showUserViews, setShowUserViews] = useState(false);
    
    const onShowHideUserViews = (event: React.MouseEvent<SVGElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setShowUserViews(!showUserViews);
    }

    useEffect(() => {
        const hasSelectedObject = (selectedObjectInfos && selectedObjectInfos?.object._key === objectInfos.object._key) || getSelectedObjectKey(workspace?._key ?? '') === objectInfos.object._key;
        if (hasSelectedObject) {
            setIsOpen(true);
        }
    }, []);

    const onArrowClick = (e: React.MouseEvent<SVGElement | HTMLDivElement>) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    }

    const handleDragStart = (e: React.DragEvent) => {
        e.dataTransfer.setData("text/plain", objectInfos.object._key);
        e.dataTransfer.effectAllowed = "move";
        onDragStart?.(objectInfos.object._key);
    };

    const calculatePosition = (y: number, height: number) => {
        if (y < height / 4) {
            return 'top';
        }

        if (y > (height * 3) / 4) {
            return 'bottom';
        }

        if (showDropLineTop) {
            return 'top';
        }

        if (showDropLineBottom) {
            return 'bottom';
        }

        return null;
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";

        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const height = rect.height;

        const position = calculatePosition(y, height);
        onDragOver?.(objectInfos.object._key, position);
    };

    const handleDragLeave = () => {
        onDragOver?.(objectInfos.object._key, null);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        onDragEnd?.();

        const draggedObjectKey = e.dataTransfer.getData("text/plain");
        if (draggedObjectKey === objectInfos.object._key) return;

        // Determine drop position
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const height = rect.height;
        const position = calculatePosition(y, height);

        if (position === null) return;

        // Call the parent drop handler
        onDrop?.(draggedObjectKey, objectInfos.object._key, position);
    };

    let icon = objectInfos.object.icon;
    if (!icon) {
        icon = defaultObjectIcon;
    }

    const hasFullObjectRight = hasGlobalRightAccess(user, userRole, ObjectRightType, FullRightAccess, objectInfos.object._key);
    const canModify = hasFullObjectRight;

    let style: React.CSSProperties = isHover ? getWsButtonStyle(wsHoverColor) : getWsButtonStyle('white');

    return (
        <>
            {showDropLineTop && <DropLineIndicator position="top" color={wsColor} />}
            <div className={TW_ObjectListItemContainer}>
                <div
                    ref={dragRef}
                    className={TW_ObjectListItem}
                    onClick={onArrowClick}
                    style={style}
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    draggable={canModify}
                    onDragStart={handleDragStart}
                    onDragEnd={onDragEnd}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <CIcon compozzIcon={icon} color="black"
                        className={`${TW_ListItemIcon} ${canModify ? 'cursor-grab' : 'cursor-default'}`} />
                    <p className={TW_ListItemName}>
                        {objectInfos.object.name}
                    </p>
                    <ObjectMenuButton objectInfo={objectInfos} showIcon={isHover} canEditObject={hasFullObjectRight} />
                    {!isOpen && <ArrowRight2 className={TW_ListItemArrow} onClick={onArrowClick} />}
                    {isOpen && <ArrowDown2 className={TW_ListItemArrow} onClick={onArrowClick} />}
                </div>
                {isOpen && hasFullObjectRight &&
                    <ObjectConfigView object={objectInfos} onMobileClose={onMobileClose} showUserViews={showUserViews} onShowHideUserViews={onShowHideUserViews} />
                }
                {isOpen && <ViewList objectInfos={objectInfos} views={objectInfos.views} onMobileClose={onMobileClose} showUserViews={showUserViews} />}
            </div>
            {showDropLineBottom && <DropLineIndicator position="bottom" color={wsColor} />}
        </>
    )
}

export default DraggableObjectListItem;
